import { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import flatpickr from "flatpickr";
import { useNavigate, useSearchParams } from "react-router";

import PageMeta from "../../../../shared/ui/common/PageMeta";
import Button from "../../../../shared/ui/button/Button";
import { useTherapist } from "../../../../context/useTherapist";
import { usePatientsForPayment } from "../../application/usePatientsForPayment";
import { useCourseLabels } from "../../../center";
import { useCreatePayment, usePayableSessions, usePaymentAccount } from "../../application/usePayments";
import {
    PaymentLineItemInput,
    defaultLineItem,
    emptyLineItem,
    isPristineLineItem,
} from "../../domain/types";
import PaymentLineItemsEditor from "../components/PaymentLineItemsEditor";
import DatePicker from "../../../../shared/ui/form/date-picker";
import PatientFinder, {
    type PatientOption,
} from "../../../../shared/ui/patient/PatientFinder";
import { formatHumanDateTime } from "../../../../shared/utils/date";

const sameLine = (a: PaymentLineItemInput, b: PaymentLineItemInput) =>
    a.description === b.description && a.quantity === b.quantity && a.unit_price === b.unit_price;

const PaymentCreatePage: React.FC = () => {
    const { t, i18n } = useTranslation();
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const { therapist } = useTherapist();
    const createPayment = useCreatePayment();

    // Both arrive when the page is opened from a session's detail view.
    const prefilledPatientId = searchParams.get("patientId") ?? "";
    // Arrives set from the session dock; otherwise the therapist may pick one,
    // or leave it off — a payment can cover several sessions or none at all.
    const [sessionId, setSessionId] = useState(searchParams.get("sessionId") ?? "");

    const [patientId, setPatientId] = useState(prefilledPatientId);
    const [notes, setNotes] = useState("");
    const [dueDate, setDueDate] = useState("");
    const [error, setError] = useState<string | null>(null);

    const [items, setItems] = useState<PaymentLineItemInput[]>([emptyLineItem()]);

    // Applied in an effect rather than as initial state because the therapist
    // arrives from context a render or two later. Only ever overwrites a line
    // nobody has typed into, so a default landing late cannot wipe work.
    //
    // Once a patient is chosen, the defaults are those of whoever charges
    // them: the centre for a patient it holds, the therapist otherwise.
    const { data: account } = usePaymentAccount(patientId || undefined);
    // The last line filled in automatically. A line still equal to it has not
    // been touched, so a better default — the centre's, once the patient
    // turns out to be the centre's — may replace it.
    const autoFilled = useRef<PaymentLineItemInput | null>(null);
    useEffect(() => {
        const source = account ?? therapist;
        const { default_payment_concept: concept, default_payment_amount: amount } =
            source ?? {};
        const next = defaultLineItem(concept, amount);
        setItems((current) => {
            if (current.length !== 1) return current;
            const [line] = current;
            const untouched =
                isPristineLineItem(line) ||
                (autoFilled.current !== null && sameLine(line, autoFilled.current));
            if (!untouched) return current;
            autoFilled.current = next;
            return [next];
        });
    }, [therapist, account]);

    const { data: patientsPage } = usePatientsForPayment(therapist?.id);
    // A training centre's student, with the course they are on under the name.
    const { courseOf } = useCourseLabels();

    // A caseload long enough to scroll is long enough to search. Same control
    // Agendar uses, so picking a patient works the same way in both tools.
    //
    // The fallback lives inside the memo: `?? []` builds a new array on every
    // render, which would defeat the memo it was a dependency of.
    const patientOptions: PatientOption[] = useMemo(
        () =>
            (patientsPage?.patients ?? []).map((patient) => ({
                id: patient.id,
                name: `${patient.first_name} ${patient.last_name}`.trim(),
                detail: courseOf(patient.course_id),
            })),
        [patientsPage, courseOf]
    );

    const { data: sessionsPage } = usePayableSessions(patientId);

    const sessions = useMemo(() => sessionsPage?.sessions ?? [], [sessionsPage]);

    const submit = () => {
        setError(null);
        createPayment.mutate(
            {
                patient_id: patientId,
                session_id: sessionId || null,
                line_items: items,
                due_date: dueDate || null,
                notes: notes || null,
            },
            {
                onSuccess: (payment) => navigate(`/payments/${payment.id}`),
                onError: () => setError(t("payments.errors.createFailed")),
            }
        );
    };

    const canSubmit =
        Boolean(patientId) &&
        items.length > 0 &&
        items.every((item) => item.description.trim().length > 0);

    return (
        <>
            <PageMeta
                title={t("payments.newPayment")}
                description={t("payments.newPayment")}
            />

            <h1 className="mb-6 text-2xl font-semibold text-content-primary">
                {t("payments.newPayment")}
            </h1>

            {error && (
                <p role="alert" className="mb-4 text-sm text-red-600">
                    {error}
                </p>
            )}

            <div className="mb-6 grid grid-cols-1 gap-4 sm:grid-cols-2">
                <div>
                    <PatientFinder
                        patients={patientOptions}
                        selectedId={patientId || null}
                        onSelect={(id) => setPatientId(id ?? "")}
                        label={t("payments.fields.recipient")}
                    />
                    {account?.managed_by_center && (
                        <p className="mt-2 text-xs text-content-tertiary">
                            {t("payments.chargedByCenter", {
                                center: account.center_name ?? "",
                            })}
                        </p>
                    )}
                </div>

                {/* Optional. A payment may cover one session, several, or none
                    at all — a package, a missed appointment — so this never
                    blocks the form. */}
                <div>
                    <label
                        htmlFor="payment-session"
                        className="mb-1 block text-sm font-medium text-content-secondary"
                    >
                        {t("payments.fields.session")}
                    </label>
                    <select
                        id="payment-session"
                        value={sessionId}
                        disabled={!patientId}
                        onChange={(e) => setSessionId(e.target.value)}
                        className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm disabled:opacity-50 dark:border-gray-700 dark:bg-gray-900 dark:text-gray-200"
                    >
                        <option value="">{t("payments.noSession")}</option>
                        {sessions.map((session) => (
                            <option key={session.id} value={session.id}>
                                {[
                                    formatHumanDateTime(session.date, i18n.language),
                                    session.title,
                                ]
                                    .filter(Boolean)
                                    .join(" · ")}
                            </option>
                        ))}
                    </select>
                </div>

                <div>
                    <DatePicker
                        id="payment-due-date"
                        label={t("payments.fields.dueDate")}
                        defaultDate={dueDate || undefined}
                        onChange={(selectedDates) =>
                            setDueDate(
                                selectedDates[0]
                                    ? flatpickr.formatDate(selectedDates[0], "Y-m-d")
                                    : ""
                            )
                        }
                    />
                </div>
            </div>

            <section className="mb-6 rounded-xl border border-gray-200 p-5 dark:border-gray-700">
                <h2 className="mb-4 text-base font-semibold text-content-primary">
                    {t("payments.lineItems")}
                </h2>
                <PaymentLineItemsEditor items={items} onChange={setItems} />
            </section>

            <div className="mb-6">
                <label
                    htmlFor="payment-notes"
                    className="mb-1 block text-sm font-medium text-content-secondary"
                >
                    {t("payments.fields.notes")}
                </label>
                <textarea
                    id="payment-notes"
                    rows={3}
                    value={notes}
                    onChange={(e) => setNotes(e.target.value)}
                    className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm dark:border-gray-700 dark:bg-gray-900 dark:text-gray-200"
                />
            </div>

            <div className="flex justify-end gap-3">
                <Button variant="outline" onClick={() => navigate("/payments")}>
                    {t("common.cancel")}
                </Button>
                <Button
                    onClick={submit}
                    disabled={!canSubmit}
                    loading={createPayment.isPending}
                >
                    {t("payments.actions.createDraft")}
                </Button>
            </div>
        </>
    );
};

export default PaymentCreatePage;
